/**
 * Short entity handles for URLs (see docs/adr: pretty URLs).
 *
 * A UUID is 128 bits; its dashed-hex rendering is 36 characters. The same bits in base62
 * are a fixed-width 22-character string — the canonical form for entity ids in URLs.
 * APIs, components and services keep speaking raw UUIDs: encoding happens only where a
 * link is built (route `params.stringify`, link builders) and decoding only where a URL
 * is read (route `params.parse`, search-param consumers via {@link normalizeEntityId}).
 *
 * Both forms are accepted everywhere a URL is read, so pre-shortener links keep working:
 * a dashed UUID is 36 chars with dashes, a short id is 22 chars from a dash-free
 * alphabet — they can never be confused.
 */

const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
const BASE = 62n
const SHORT_ID_LENGTH = 22
const UUID_BITS = 1n << 128n

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const SHORT_ID_RE = /^[0-9A-Za-z]{22}$/

export function isUuid(value: string): boolean {
  return UUID_RE.test(value)
}

export function isShortEntityId(value: string): boolean {
  return SHORT_ID_RE.test(value)
}

/** Dashed UUID → fixed-width 22-char base62. Throws on non-UUID input (caller bug). */
export function encodeUuid(uuid: string): string {
  if (!isUuid(uuid)) throw new Error(`encodeUuid: not a UUID: ${uuid}`)
  let n = BigInt('0x' + uuid.replace(/-/g, ''))
  let out = ''
  while (n > 0n) {
    out = ALPHABET[Number(n % BASE)] + out
    n /= BASE
  }
  return out.padStart(SHORT_ID_LENGTH, '0')
}

/**
 * 22-char base62 → lowercase dashed UUID, or undefined if the string decodes to more
 * than 128 bits (22 base62 chars can exceed a UUID; such strings are not valid ids).
 */
export function decodeShortId(shortId: string): string | undefined {
  if (!isShortEntityId(shortId)) return undefined
  let n = 0n
  for (const ch of shortId) n = n * BASE + BigInt(ALPHABET.indexOf(ch))
  if (n >= UUID_BITS) return undefined
  const hex = n.toString(16).padStart(32, '0')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * The read-side chokepoint: accepts either URL form and returns the canonical UUID.
 * Anything that is neither form returns undefined (fail closed — never hand a
 * malformed id to an API call).
 */
export function normalizeEntityId(value: string | null | undefined): string | undefined {
  if (!value) return undefined
  if (isUuid(value)) return value.toLowerCase()
  return decodeShortId(value)
}

/**
 * The write-side chokepoint: canonical short form for a link. A raw UUID is encoded;
 * an already-short id passes through; anything else passes through unchanged (fail
 * open — a link with an odd value 404s downstream, which beats throwing mid-render).
 */
export function toShortEntityId(value: string): string {
  return isUuid(value) ? encodeUuid(value) : value
}
